import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../utils/api';
import { DuotoneIcon } from '../../components/staff/ui/DuotoneIcon';
import './Staff.css';
import './StaffMessages.css';

interface StaffMessage {
  id: number;
  content: string;
  contact: string | null;
  status: string;
  read_at: string | null;
  created_at: string;
}

type Filter = 'unread' | 'all';

/** "2026-07-04 18:30:00" (UTC SQLite) → "4 juil. 18:30". */
function fmtDate(iso: string): string {
  // SQLite datetime('now') est UTC sans suffixe ; on force l'interprétation UTC.
  const d = new Date(iso.replace(' ', 'T') + 'Z');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('fr-FR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function StaffMessages() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<StaffMessage[]>([]);
  const [unread, setUnread] = useState(0);
  const [filter, setFilter] = useState<Filter>('unread');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const flash = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2000);
  };

  const load = useCallback(async (f: Filter) => {
    setLoading(true);
    try {
      const res = await apiFetch(`/staff-messages?status=${f}&limit=100`);
      if (res.ok) {
        const data = await res.json();
        setMessages(Array.isArray(data.messages) ? data.messages : []);
        setUnread(typeof data.unread === 'number' ? data.unread : 0);
      } else {
        setMessages([]);
      }
    } catch {
      setMessages([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load(filter);
  }, [filter, load]);

  const markRead = async (m: StaffMessage) => {
    if (m.status !== 'new') return;
    setBusyId(m.id);
    try {
      const res = await apiFetch(`/staff-messages/${m.id}/read`, { method: 'PATCH' });
      if (res.ok) {
        flash('Marqué comme lu');
        // Optimiste : retire des non-lus, décrémente le compteur.
        setUnread((u) => Math.max(0, u - 1));
        if (filter === 'unread') {
          setMessages((list) => list.filter((x) => x.id !== m.id));
        } else {
          setMessages((list) =>
            list.map((x) => (x.id === m.id ? { ...x, status: 'read', read_at: new Date().toISOString() } : x)),
          );
        }
      } else {
        flash('Échec');
      }
    } catch {
      flash('Erreur réseau');
    }
    setBusyId(null);
  };

  return (
    <div className="staff-page staff-messages">
      <div className="staff-header">
        <button className="staff-back" onClick={() => navigate('/staff/dashboard')}>‹</button>
        <h1>Messages</h1>
        {unread > 0 && <span className="sm-header-badge">{unread}</span>}
      </div>

      <div className="sm-filter-pills">
        <button
          className={`sm-pill${filter === 'unread' ? ' active' : ''}`}
          onClick={() => setFilter('unread')}
        >
          Non lus{unread > 0 ? ` (${unread})` : ''}
        </button>
        <button
          className={`sm-pill${filter === 'all' ? ' active' : ''}`}
          onClick={() => setFilter('all')}
        >
          Tous
        </button>
      </div>

      {toast && <div className="sm-toast">{toast}</div>}

      {loading ? (
        <div className="sm-empty">Chargement…</div>
      ) : messages.length === 0 ? (
        <div className="sm-empty">
          <DuotoneIcon name="comment-dots" size={40} className="sm-empty-icon" />
          <p>{filter === 'unread' ? 'Aucun message non lu' : 'Aucun message'}</p>
        </div>
      ) : (
        <div className="sm-list">
          {messages.map((m) => {
            const isNew = m.status === 'new';
            return (
              <div
                key={m.id}
                className={`sm-card${isNew ? ' sm-card-new' : ''}`}
                onClick={() => markRead(m)}
              >
                <div className="sm-card-head">
                  {isNew && <span className="sm-dot" aria-label="non lu" />}
                  <span className="sm-date">{fmtDate(m.created_at)}</span>
                  {m.contact && <span className="sm-contact">{m.contact}</span>}
                </div>
                <p className="sm-content">{m.content}</p>
                {isNew && (
                  <button
                    className="sm-mark-btn"
                    disabled={busyId === m.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      markRead(m);
                    }}
                  >
                    {busyId === m.id ? '…' : 'Marquer comme lu'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
